import { useEffect } from 'react';
import { getSubdomainFromHost, isOrganizationSubdomain } from '../lib/hostOrg';
import { DEFAULT_LOGO_DIMENSIONS } from '../constants/logo';
import { useOrganization, useOrganizationBySubdomain, useUserAccess } from './queries/useUserData';

/**
 * Resolve branding from the current org subdomain or, for signed-in users on
 * the default host, from the user's organization.
 */
export function useOrganizationLogo(userId = null) {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const currentSubdomain = getSubdomainFromHost(hostname);
  const isOrgHost = isOrganizationSubdomain(currentSubdomain);

  const {
    data: subdomainOrg,
    isFetched: isSubdomainOrgFetched,
  } = useOrganizationBySubdomain(isOrgHost ? currentSubdomain : undefined);

  const {
    data: userAccessData,
    isFetched: isUserAccessFetched,
    isLoading: isUserAccessLoading,
  } = useUserAccess(!isOrgHost ? userId : undefined);

  const userOrgName = !isOrgHost ? userAccessData?.organization || null : null;
  const {
    data: userOrgData,
    isFetched: isUserOrgFetched,
    isLoading: isUserOrgLoading,
  } = useOrganization(!isOrgHost && userOrgName ? userOrgName : undefined);

  const organization = subdomainOrg || userOrgData || null;
  const logo1Dimensions = organization?.logo_width && organization?.logo_height
    ? { width: organization.logo_width, height: organization.logo_height }
    : DEFAULT_LOGO_DIMENSIONS;
  const logo2Dimensions = organization?.logo2_width && organization?.logo2_height
    ? { width: organization.logo2_width, height: organization.logo2_height }
    : null;

  const isResolvingUserBranding = Boolean(userId) && !isOrgHost && (
    isUserAccessLoading || (Boolean(userOrgName) && isUserOrgLoading)
  );
  const hasResolved = isOrgHost
    ? isSubdomainOrgFetched
    : (!userId || (isUserAccessFetched && (!userOrgName || isUserOrgFetched)));
  const shouldHideLogo = isOrgHost
    ? !hasResolved
    : isResolvingUserBranding;

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    if (organization?.name && (isOrgHost || organization?.subdomain)) {
      document.title = `${organization.name} XR`;
      return;
    }

    document.title = 'AstraXR - Learn. Evolve. Transcend.';
  }, [organization?.name, organization?.subdomain, isOrgHost]);

  return {
    organization,
    orgName: organization?.name || null,
    features: organization?.features || null,
    logo1: organization?.logo_url || null,
    logo2: organization?.logo_url_2 || null,
    logo1Dimensions,
    logo2Dimensions,
    isOrgHost,
    isResolving: shouldHideLogo,
    hasResolved,
    shouldHideLogo,
  };
}
